import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../contexts/LanguageContext';
import { availableLanguages } from '../translations';
import { backendUrl } from '../config';
import { getUserTimezone } from '../utils/timezoneUtils';

const ProfilePage = ({ session, userPlan }) => {
  const [deleting, setDeleting] = useState(false);
  // Remove local userPlan and loadingPlan state
  const { currentLanguage, changeLanguage, t, isRTL } = useLanguage();
  const user = session?.user || session;
  const email = user?.email || 'Unknown';
  const name = user?.user_metadata?.full_name || 'Not set';
  const createdAt = user?.created_at
    ? new Date(user.created_at).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
    : 'Unknown';

  // Remove useEffect that fetches user plan

  const formatExpiryDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const userTimezone = getUserTimezone();
    return date.toLocaleDateString(undefined, {
      timeZone: userTimezone,
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleLanguageChange = async (event) => {
    const newLanguage = event.target.value;
    await changeLanguage(newLanguage);
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm(t('areYouSureDeleteAccount'))) return;
    setDeleting(true);
    try {
      // Get the current session and access token (Supabase v2+)
      const { data: { session: sessionObj } } = await supabase.auth.getSession();
      const user = sessionObj?.user;
      const accessToken = sessionObj?.access_token;

      // Call your backend endpoint
      const res = await fetch(`${backendUrl}/auth/delete-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, accessToken }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to delete account');

      // Log out and redirect
      await supabase.auth.signOut();
      window.location.href = '/home';
    } catch (err) {
      console.error('Error deleting account:', err);
      alert(t('failedToDeleteAccount') + ' ' + (err.message || err.error_description || err));
      setDeleting(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const { data: { session: sessionObj } } = await supabase.auth.getSession();
      const accessToken = sessionObj?.access_token;

      const res = await fetch(`${backendUrl}/create-portal-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken }),
      });

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (parseErr) {
        throw new Error('Invalid server response');
      }

      if (!res.ok) {
        if (res.status === 404 || data.error === 'NO_SUBSCRIPTIONS') {
          alert(t('noSubscriptionsToManage') || 'You have not yet purchased anything and have no subscriptions to manage.');
        } else {
          alert(t('failedToManageSubscription') + ' ' + (data.error || data.message || 'Unknown error'));
        }
        return;
      }

      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(t('failedToManageSubscription') + ' ' + (data.error || data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error managing subscription:', err);
      alert(t('failedToManageSubscription') + ' ' + err.message);
    }
  };

  return (
    <div className="card" dir={isRTL ? 'rtl' : 'ltr'} style={{ maxWidth: 480, margin: '2rem auto', padding: '2rem', textAlign: isRTL ? 'right' : 'left' }}>
      <h2>{t('myProfile')}</h2>
      <div style={{ marginBottom: '1.2rem' }}>
        <strong>{t('email')}:</strong> <span>{email}</span>
      </div>
      <div style={{ marginBottom: '1.2rem' }}>
        <strong>{t('name')}:</strong> <span>{name}</span>
      </div>
      <div style={{ marginBottom: '1.2rem' }}>
        <strong>{t('appLanguage')}:</strong>
        <select
          value={currentLanguage}
          onChange={handleLanguageChange}
          style={{
            marginLeft: '0.5rem',
            padding: '0.5rem',
            borderRadius: '4px',
            border: '1px solid #ddd',
            fontSize: '1rem',
            minWidth: '150px'
          }}
        >
          {availableLanguages.map(lang => {
            const languageNames = {
              en: { en: 'English', es: 'Spanish', fr: 'French', he: 'Hebrew', zh: 'Mandarin', ko: 'Korean' },
              es: { en: 'Inglés', es: 'Español', fr: 'Francés', he: 'Hebreo', zh: 'Mandarín', ko: 'Coreano' },
              fr: { en: 'Anglais', es: 'Espagnol', fr: 'Français', he: 'Hébreu', zh: 'Mandarin', ko: 'Coréen' },
              he: { en: 'אנגלית', es: 'ספרדית', fr: 'צרפתית', he: 'עברית', zh: 'מנדרינית', ko: 'קוריאנית' },
              zh: { en: '英语', es: '西班牙语', fr: '法语', he: '希伯来语', zh: '中文', ko: '韩语' }
            };
            const currentLanguageName = languageNames[currentLanguage]?.[lang.code] || lang.name;
            return (
              <option key={lang.code} value={lang.code}>
                {lang.nativeName} ({currentLanguageName})
              </option>
            );
          })}
        </select>
      </div>

      <div style={{ marginBottom: '1.2rem' }}>
        <strong>{t('accountCreated')}:</strong> <span>{createdAt}</span>
      </div>

      <div style={{ marginBottom: '1.2rem' }}>
        <strong>{t('currentPlan')}:</strong> <span>
          {userPlan ? (
            <>
              {userPlan.plan === 'pro' ? t('planPro') : t('planFree')}
              {userPlan.plan === 'pro' && userPlan.premiumExpiresAt && (
                <span style={{ color: '#666', fontSize: '0.9em' }}>
                  {' '}({t('expiresOn')} {formatExpiryDate(userPlan.premiumExpiresAt)})
                </span>
              )}
            </>
          ) : (
            session ? 'Loading...' : 'Unknown'
          )}
        </span>
        {userPlan?.plan === 'pro' && (
          <div style={{ marginTop: '0.6rem' }}>
            <button
              onClick={handleManageSubscription}
              style={{
                background: '#f1f8e9',
                border: '1px solid #b7e4c7',
                color: '#386641',
                borderRadius: '6px',
                padding: '5px 14px',
                fontSize: '0.85rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#e9f5e1'}
              onMouseLeave={(e) => e.target.style.background = '#f1f8e9'}
            >
              {t('manageSubscription')}
            </button>
          </div>
        )}
      </div>

      <div style={{ color: '#888', fontSize: '0.95em', marginBottom: '2rem' }}>
        {t('profileEditingComingSoon')}
      </div>
      <button
        className="btn btn-danger"
        style={{ background: '#e74c3c', color: '#fff', width: '100%', padding: '0.9rem', fontWeight: 600, fontSize: '1.1rem', borderRadius: 8, border: 'none', marginTop: '1.5rem', cursor: deleting ? 'not-allowed' : 'pointer', opacity: deleting ? 0.7 : 1 }}
        onClick={handleDeleteAccount}
        disabled={deleting}
      >
        {deleting ? t('deletingAccount') : t('deleteAccount')}
      </button>
    </div>
  );
};

export default ProfilePage; 